// Google Analytics 4 (gtag.js) loader. Idempotent: calling load() twice is safe.
// Only invoked after the user has granted consent.
//
// Page URLs are scrubbed (redact.ts): gtag would otherwise record
// document.location, which holds order ids on the order pages. page_location
// and page_referrer are set to the redacted URL at config time and again on
// every SPA route change (syncGaPage).
//
// Google consent mode v2 (BUG-527, plan G1): the cookie banner asks for
// statistics consent only, so ad_storage, ad_user_data and ad_personalization
// are always denied. analytics_storage defaults to denied and is updated to
// granted by the banner's Accept, and back to denied if consent is withdrawn
// (applyGoogleConsent, called by index.ts before any Google tag loads).

import type { ConsentStatus } from '../consent'
import { isInternalTraffic } from './internal'
import { redactUrl } from './redact'

const DEFAULT_GA4_ID = 'G-GT2VKJ3NG3'
const SCRIPT_ATTR = 'data-helin-ga'

interface GtagWindow extends Window {
  dataLayer?: Array<unknown>
  gtag?: (...args: unknown[]) => void
}

export function getGa4Id(): string {
  const fromEnv = (import.meta.env.VITE_GA4_ID as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_GA4_ID
}

export function isGa4Loaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

/** The window's gtag, created as Google's snippet does when it is missing. */
function ensureGtag(): (...args: unknown[]) => void {
  const w = window as GtagWindow
  w.dataLayer = w.dataLayer ?? []
  // gtag forwards every call as an arguments-list push onto dataLayer; this
  // matches the snippet Google ships and is what GA expects.
  w.gtag =
    w.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments)
    }
  return w.gtag
}

/** Ad signals: always denied (no marketing consent exists). */
const ADS_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const

let consentDefaulted = false

/**
 * Google consent mode for the banner's decision. The first call with consent
 * granted sets the defaults (everything denied) and then updates
 * analytics_storage to granted; later calls update it either way. Before any
 * grant it does nothing: no Google tag loads then, so nothing is left behind.
 */
export function applyGoogleConsent(status: ConsentStatus): void {
  if (!consentDefaulted) {
    if (status !== 'granted') return
    ensureGtag()('consent', 'default', { ...ADS_DENIED, analytics_storage: 'denied' })
    consentDefaulted = true
  }
  ensureGtag()('consent', 'update', {
    ...ADS_DENIED,
    analytics_storage: status === 'granted' ? 'granted' : 'denied',
  })
}

export function _resetGoogleConsentForTesting(): void {
  consentDefaulted = false
}

export function loadGa4(id: string = getGa4Id()): void {
  if (isGa4Loaded()) return
  if (!id) return

  const gtag = ensureGtag()
  const page = {
    page_location: redactUrl(window.location.href),
    page_referrer: redactUrl(document.referrer),
    // Owner and staff browsers marked with ?internal=1 (internal.ts).
    ...(isInternalTraffic() ? { traffic_type: 'internal' } : {}),
  }
  gtag('js', new Date())
  // `set` makes the redacted page apply to every later hit, `config` to the
  // first page view it sends.
  gtag('set', page)
  gtag('config', id, page)

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  script.setAttribute(SCRIPT_ATTR, id)
  document.head.appendChild(script)
}

/**
 * SPA route change: point GA4 at the new (redacted) page and send its page
 * view. `referrer` is the previous in-app URL. No-op until GA4 is loaded.
 */
export function syncGaPage(url: string, referrer: string): void {
  const w = window as GtagWindow
  if (!w.gtag || !isGa4Loaded()) return
  const page = { page_location: redactUrl(url), page_referrer: redactUrl(referrer) }
  w.gtag('set', page)
  w.gtag('event', 'page_view', page)
}
